import { jsPDF } from 'jspdf';
import { OrderCompletionChecklist, OrderPriority } from '../../types/order';
import { getPdfBackgroundDataUrl } from './pdfBackground';

export type OrderCompletionPdfData = {
  protocolNumber?: string | null;
  address: string;
  neighborhood?: string | null;
  reporterName?: string | null;
  houseNumberInFront?: string | null;
  referencePoint?: string | null;
  poleBusbar?: string | null;
  description?: string | null;
  priority?: OrderPriority;
  createdAt?: string;
  updatedAt?: string;
  checklist?: OrderCompletionChecklist | null;
};

const priorityLabel: Record<OrderPriority, string> = {
  ALTA: 'Alta',
  MEDIA: 'Media',
  BAIXA: 'Baixa',
};

const formatDateTime = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCoord = (lat?: number | null, lng?: number | null): string => {
  if (lat == null || lng == null) return '-';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

export const buildOrderCompletionPdf = async (
  data: OrderCompletionPdfData,
): Promise<jsPDF> => {
  const document = new jsPDF({ unit: 'mm', format: 'a4' });
  const contentTopOffset = 25;
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const backgroundDataUrl = await getPdfBackgroundDataUrl();

  if (backgroundDataUrl) {
    document.addImage(backgroundDataUrl, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  }

  const drawCard = (
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    value: string,
    maxLines = 2,
  ) => {
    document.setDrawColor(222, 226, 230);
    document.setFillColor(248, 249, 250);
    document.roundedRect(x, y, width, height, 2, 2, 'FD');

    document.setTextColor(73, 80, 87);
    document.setFont('helvetica', 'bold');
    document.setFontSize(7.5);
    document.text(label, x + 2.5, y + 3.5);

    document.setTextColor(33, 37, 41);
    document.setFont('helvetica', 'normal');
    document.setFontSize(8);

    const textWidth = width - 6;
    const allLines = document.splitTextToSize(value || '-', textWidth);
    const lines = allLines.slice(0, maxLines);
    if (allLines.length > maxLines && lines.length > 0) {
      lines[lines.length - 1] = `${lines[lines.length - 1]}...`;
    }
    document.text(lines, x + 2.5, y + 8);
  };

  // ── Cabeçalho ────────────────────────────────────────────────────────────
  document.setFillColor(0, 65, 184);
  document.roundedRect(margin, 10 + contentTopOffset, contentWidth, 12, 3, 3, 'F');
  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(11);
  document.text('Comprovante de Conclusao', margin + 5, 16 + contentTopOffset);
  document.setFontSize(7.5);
  document.text('Sistema de Iluminacao Publica', margin + 5, 19 + contentTopOffset);

  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(9);
  document.text(
    data.protocolNumber?.trim() || '-',
    pageWidth - margin - 5,
    16 + contentTopOffset,
    { align: 'right' },
  );
  document.setFont('helvetica', 'normal');
  document.setFontSize(7);
  document.text(
    formatDateTime(data.updatedAt),
    pageWidth - margin - 5,
    19 + contentTopOffset,
    { align: 'right' },
  );

  const colGap = 4;
  const colWidth = (contentWidth - colGap) / 2;

  // ── Dados da OS ──────────────────────────────────────────────────────────
  let y = 26 + contentTopOffset;
  drawCard(margin, y, colWidth, 11, 'PROTOCOLO', data.protocolNumber?.trim() || '-', 1);
  drawCard(margin + colWidth + colGap, y, colWidth, 11, 'SOLICITANTE', data.reporterName?.trim() || '-', 1);
  y += 14;
  drawCard(margin, y, contentWidth, 13, 'ENDERECO', data.address.trim() || '-', 2);
  y += 16;
  drawCard(margin, y, colWidth, 11, 'BAIRRO', data.neighborhood?.trim() || '-', 1);
  drawCard(margin + colWidth + colGap, y, colWidth, 11, 'CASA EM FRENTE', data.houseNumberInFront?.trim() || '-', 1);
  y += 14;
  drawCard(margin, y, colWidth, 11, 'REFERENCIA', data.referencePoint?.trim() || '-', 1);
  drawCard(margin + colWidth + colGap, y, colWidth, 11, 'BARRAMENTO', data.poleBusbar?.trim() || '-', 1);
  y += 14;
  drawCard(margin, y, colWidth, 11, 'PRIORIDADE', data.priority ? priorityLabel[data.priority] : '-', 1);
  drawCard(margin + colWidth + colGap, y, colWidth, 11, 'ABERTURA', formatDateTime(data.createdAt) || '-', 1);
  y += 14;

  // ── Checklist ────────────────────────────────────────────────────────────
  const cl = data.checklist;
  drawCard(margin, y, colWidth, 11, 'LAMPADA ENCONTRADA', cl?.lampadaEncontrada || '-', 1);
  drawCard(margin + colWidth + colGap, y, colWidth, 11, 'LAMPADA INSTALADA', cl?.lampadaInstalada || '-', 1);
  y += 14;
  drawCard(margin, y, contentWidth, 11, 'REFLETOR INSTALADO', cl?.refletorInstalado || '-', 1);
  y += 14;

  // Materiais
  const materiais = cl?.materiaisUtilizados ?? [];
  const materiaisQtd = cl?.materiaisQtd ?? {};
  const materiaisText =
    materiais.length === 0
      ? '-'
      : materiais.map((m) => `${m} x${materiaisQtd[m] ?? 1}`).join(', ');
  document.setFont('helvetica', 'bold');
  document.setFontSize(8);
  document.setTextColor(73, 80, 87);
  document.text('Materiais utilizados:', margin, y + 4);
  y += 7;
  document.setFont('helvetica', 'normal');
  document.setFontSize(8);
  document.setTextColor(33, 37, 41);
  const matLines = document.splitTextToSize(materiaisText || '-', contentWidth) as string[];
  document.text(matLines.slice(0, 6), margin, y + 3);
  y += matLines.length > 6 ? 22 : 8;

  // Observações
  if (cl?.observacoes?.trim()) {
    document.setFont('helvetica', 'bold');
    document.setFontSize(8);
    document.setTextColor(73, 80, 87);
    document.text('Observacoes:', margin, y + 3);
    y += 6;
    document.setFont('helvetica', 'normal');
    document.setFontSize(8);
    document.setTextColor(33, 37, 41);
    const obsLines = document.splitTextToSize(cl.observacoes.trim(), contentWidth) as string[];
    document.text(obsLines.slice(0, 4), margin, y + 3);
    y += obsLines.length > 4 ? 14 : 6;
  }

  // Técnicos responsáveis
  if (cl?.tecnicos?.length) {
    document.setFont('helvetica', 'bold');
    document.setFontSize(8);
    document.setTextColor(73, 80, 87);
    document.text('Tecnicos responsaveis:', margin, y + 3);
    y += 6;
    document.setFont('helvetica', 'normal');
    document.setFontSize(8);
    document.setTextColor(33, 37, 41);
    document.text(cl.tecnicos.join(', '), margin, y + 3);
    y += 6;
  }

  // ── Foto do poste ────────────────────────────────────────────────────────
  if (cl?.fotoPosteUrl) {
    const photoDataUrl = await fetchImageAsDataUrl(cl.fotoPosteUrl);
    if (photoDataUrl) {
      const photoWidth = 55;
      const photoHeight = 42;
      const photoX = margin;
      const photoY = Math.min(y + 4, pageHeight - margin - photoHeight - 30);
      try {
        const props = document.getImageProperties(photoDataUrl);
        const format = props.fileType ? props.fileType.toUpperCase() : 'JPEG';
        document.addImage(photoDataUrl, format, photoX, photoY, photoWidth, photoHeight, undefined, 'FAST');
        document.setDrawColor(206, 212, 218);
        document.rect(photoX, photoY, photoWidth, photoHeight);
        document.setFont('helvetica', 'normal');
        document.setFontSize(7);
        document.setTextColor(73, 80, 87);
        document.text(
          `Foto do poste - ${formatCoord(cl.fotoPosteLat, cl.fotoPosteLng)}`,
          photoX,
          photoY + photoHeight + 4,
        );
      } catch {
        // ignora falha ao inserir imagem
      }
    }
  }

  // ── Rodapé ───────────────────────────────────────────────────────────────
  const footerY = pageHeight - margin - 10;
  document.setDrawColor(206, 212, 218);
  document.line(margin, footerY, pageWidth - margin, footerY);
  document.setFont('helvetica', 'normal');
  document.setFontSize(7);
  document.setTextColor(128, 128, 128);
  document.text(`Gerado em: ${formatDateTime(data.updatedAt) || '-'}`, margin, footerY + 4);
  document.text(data.protocolNumber?.trim() || '-', pageWidth - margin, footerY + 4, {
    align: 'right',
  });
  document.setFontSize(6);
  document.setTextColor(150, 150, 150);
  document.text('Desenvolvido por SICTEC', pageWidth / 2, footerY + 7, { align: 'center' });

  return document;
};
